/**
 * Utilities for Plugin Function entity type: path building, selection, and $pivot action.
 * API: GET .../entities/pluginfunctions/{entityId}/$pivot
 */

export const PLUGIN_FUNCTIONS_SEGMENT = 'entities/pluginfunctions';
export const PIVOT_ACTION = '$pivot';

/** Entity type name for plugin functions (case-insensitive check). */
export const PLUGIN_FUNCTION_ENTITY_TYPE = 'PluginFunction';

/**
 * Whether the given entity type is PluginFunction.
 */
export function isPluginFunctionEntityType(entityType: string | null | undefined): boolean {
    return entityType?.trim().toLowerCase() === 'pluginfunction';
}

/**
 * Relative path for the $pivot endpoint for a plugin function entity.
 * Example: "entities/pluginfunctions/NAASGQIY37CS.BKJS/$pivot"
 */
export function getPluginFunctionPivotPath(entityId: string): string {
    if (!entityId?.trim()) {
        throw new Error('Plugin function entity ID is required for $pivot path.');
    }
    return `${PLUGIN_FUNCTIONS_SEGMENT}/${encodeURIComponent(entityId.trim())}/${PIVOT_ACTION}`;
}

/**
 * Full path with leading slash for use with axios baseURL (e.g. /api/scim/v2).
 * Example: "/entities/pluginfunctions/NAASGQIY37CS.BKJS/$pivot"
 */
export function getPluginFunctionPivotApiPath(entityId: string): string {
    return `/${getPluginFunctionPivotPath(entityId)}`;
}

/**
 * Relative path for the $pivot endpoint with a specific entity ID.
 * Example: "entities/pluginfunctions/NAASGQIY37CS.BKJS/$pivot/ABC123"
 */
export function getPluginFunctionPivotEntityPath(pluginId: string, entityId: string): string {
    if (!pluginId?.trim()) throw new Error('Plugin function ID is required.');
    if (!entityId?.trim()) throw new Error('Entity ID is required for $pivot/{entityId}.');
    return `${PLUGIN_FUNCTIONS_SEGMENT}/${encodeURIComponent(pluginId.trim())}/${PIVOT_ACTION}/${encodeURIComponent(entityId.trim())}`;
}

/**
 * Full path with leading slash for $pivot with entity.
 * Example: "/entities/pluginfunctions/NAASGQIY37CS.BKJS/$pivot/ABC123"
 */
export function getPluginFunctionPivotEntityApiPath(pluginId: string, entityId: string): string {
    return `/${getPluginFunctionPivotEntityPath(pluginId, entityId)}`;
}

const DETAILS_ACTION = '$details';

/**
 * Relative path for $pivot/{entityId}/$details/{detailId}.
 * Example: "entities/pluginfunctions/NAASGQIY37CS.BKJS/$pivot/8861/$details/20241035"
 */
export function getPluginFunctionPivotDetailsPath(pluginId: string, entityId: string, detailId: string): string {
    if (!pluginId?.trim() || !entityId?.trim() || !detailId?.trim()) {
        throw new Error('Plugin ID, entity ID, and detail ID are required for $details.');
    }
    return `${getPluginFunctionPivotEntityPath(pluginId, entityId)}/${DETAILS_ACTION}/${encodeURIComponent(detailId.trim())}`;
}

/**
 * Full path with leading slash for $details.
 * Example: "/entities/pluginfunctions/NAASGQIY37CS.BKJS/$pivot/8861/$details/20241035"
 */
export function getPluginFunctionPivotDetailsApiPath(pluginId: string, entityId: string, detailId: string): string {
    return `/${getPluginFunctionPivotDetailsPath(pluginId, entityId, detailId)}`;
}

const INVOKE_ACTION = '$invoke';

/**
 * Relative path for $pivot/{entityId}/$invoke/{targetId}.
 * targetId is the id of the thing selected on the screen before (e.g. detail id from the URL).
 * Example: "entities/pluginfunctions/NAASGQIY37CS.BKJS/$pivot/8861/$invoke/20220030"
 */
export function getPluginFunctionInvokePath(pluginId: string, pivotEntityId: string, targetId: string): string {
    if (!pluginId?.trim() || !pivotEntityId?.trim() || !targetId?.trim()) {
        throw new Error('Plugin ID, pivot entity ID, and target ID are required for $invoke.');
    }
    return `${getPluginFunctionPivotEntityPath(pluginId, pivotEntityId)}/${INVOKE_ACTION}/${encodeURIComponent(targetId.trim())}`;
}

/**
 * Full path with leading slash for $invoke (POST with body).
 * Example: "/entities/pluginfunctions/NAASGQIY37CS.BKJS/$pivot/8861/$invoke/20220030"
 */
export function getPluginFunctionInvokeApiPath(pluginId: string, pivotEntityId: string, targetId: string): string {
    return `/${getPluginFunctionInvokePath(pluginId, pivotEntityId, targetId)}`;
}

/**
 * Normalize plugin-function list payloads for table rendering.
 *
 * **Root `$pivot`** (`GET .../entities/pluginfunctions/{id}/$pivot`): the response body is a **JSON array**
 * of row objects (no wrapper object, no `Resources` key).
 *
 * **SCIM ListResponse** (RFC 7644): when the body is an object, the only supported list field is **`Resources`**
 * (used by some paginated SCIM endpoints such as `$pivot/{entityId}` when the server returns a list envelope).
 */
export function extractPluginFunctionListItems(payload: unknown): unknown[] {
    if (payload == null) {
        return [];
    }
    if (Array.isArray(payload)) {
        return payload;
    }
    if (typeof payload !== 'object') {
        return [];
    }
    const resources = (payload as Record<string, unknown>).Resources;
    if (Array.isArray(resources)) {
        return resources;
    }
    return [];
}
